import { api } from "@/lib/api";
import type { OrganizationDetail } from "@/types";
import { formatDate } from "@/lib/utils";
import { Badge, Text } from "@a7y/tailwind-catalyst";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Check, Copy, FolderOpen } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PROJECT_STATUS_COLORS } from "./org-detail/constants";

function MetadataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-4 border-b border-zinc-100 py-3 dark:border-zinc-800">
      <Text className="text-sm font-medium text-zinc-500 dark:text-zinc-400">{label}</Text>
      <div className="col-span-2 text-sm">{value}</div>
    </div>
  );
}

export function ProjectDetailPage() {
  const { orgId, projectId } = useParams<{ orgId: string; projectId: string }>();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery<OrganizationDetail>({
    queryKey: ["organization-detail", orgId],
    queryFn: () => api.getOrganizationDetail(orgId!),
    enabled: !!orgId,
  });

  const handleCopyId = () => {
    if (!projectId) return;
    navigator.clipboard.writeText(projectId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
      </div>
    );
  }

  const project = data?.projects?.find((p) => p.id === projectId);

  if (error || !data || !project) {
    return (
      <div className="px-4 py-2">
        <button
          onClick={() => navigate(`/admin/organizations/${orgId}?tab=projects`)}
          className="mb-4 inline-flex items-center text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Organization
        </button>
        <div className="mt-8 text-center">
          <FolderOpen className="mx-auto h-12 w-12 text-gray-400" />
          <Text className="mt-2 text-gray-500 dark:text-zinc-400">Project not found</Text>
        </div>
      </div>
    );
  }

  const platforms: string[] = [];
  if (project.apps?.asc) platforms.push("App Store");
  if (project.apps?.gpc) platforms.push("Play Store");
  const platformLabel = platforms.length > 0 ? platforms.join(", ") : "—";

  return (
    <div className="px-4 py-2">
      <button
        onClick={() => navigate(`/admin/organizations/${orgId}?tab=projects`)}
        className="mb-4 inline-flex items-center text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Organization
      </button>

      <div className="mb-8 flex items-center gap-4">
        {project.iconUrl ? (
          <img
            src={project.iconUrl}
            alt=""
            className="h-12 w-12 rounded-lg object-cover"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-700">
            <FolderOpen className="h-6 w-6 text-zinc-400" />
          </div>
        )}
        <div>
          <Text className="text-3xl font-bold text-zinc-950 dark:text-white">{project.name}</Text>
          <div className="flex items-center gap-1.5">
            <Text className="font-mono text-sm text-zinc-400 dark:text-zinc-500">{project.id}</Text>
            <button
              onClick={handleCopyId}
              className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
              title="Copy project ID"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>
        <div className="ml-auto">
          <Badge color={PROJECT_STATUS_COLORS[project.status] ?? "zinc"}>
            {project.status.toUpperCase()}
          </Badge>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white px-6 py-2 dark:border-zinc-700 dark:bg-zinc-800">
        <MetadataRow label="Project ID" value={<span className="font-mono text-xs">{project.id}</span>} />
        <MetadataRow label="Name" value={project.name} />
        <MetadataRow
          label="Organization"
          value={
            <button
              onClick={() => navigate(`/admin/organizations/${orgId}`)}
              className="text-blue-600 hover:underline dark:text-blue-400"
            >
              {data.organization.name}
            </button>
          }
        />
        <MetadataRow label="Status" value={project.status} />
        <MetadataRow label="Platform" value={platformLabel} />
        <MetadataRow
          label="Target locales"
          value={
            project.targetLocales.length > 0
              ? project.targetLocales.join(", ")
              : "—"
          }
        />
        <MetadataRow label="Created" value={formatDate(project.createdAt)} />
      </div>
    </div>
  );
}
